import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
	useRecoilState,
	useRecoilValue,
	useResetRecoilState,
	useSetRecoilState,
} from 'recoil';

import { Button, Confirmation, Image, Loader } from '@storybook';
import Modal from '@storybook/new-modal/modal';
import { defineds } from '@storybook/date-range-picker/defineds';
import { DatePicker } from 'components';

import { API_URL } from 'constant';
import {
	ActiveActionKeyNameState,
	ActiveStepState,
	AllConditionalPipelinesState,
	AllOpenedStepsNameState,
	AllPipelinesState,
	BusinessPaymentCardState,
	ClientsCredentialsState,
	CredentialsLoadedState,
	FilterSortTypeTableState,
	FundPaginationState,
	IframePropsState,
	OpenInviteSelectorModal,
	PipelineIdState,
	PipelineStepsState,
	SelectedDateRangeState,
	SelectedOnboardingAction,
	SelectedPipelineDetails,
	SelectedPipelineState,
	isChecqueFraudSelected,
	loginState,
	pipelinesFiltersState,
	useSignedDocs,
} from 'global-stores';
import { useTrackEvents } from 'helpers';
import {
	useAllowedInvites,
	useFirebaseInit,
	useFreePlan,
	useNetwork,
	useNotification,
	useOutsideClick,
	useSubscription,
	useTimezoneDate,
} from 'hooks';
import { ROUTES } from 'routes';
import { getOffDate } from 'utils';
import { acquireOnboardingLock } from 'utils/onboardingLock';
import { useComplexLayout } from 'views/complex-onboarding-flow';
import {
	ComplexFlowUnconfigedState,
	EdgesState,
	NodesState,
} from 'views/complex-onboarding-flow/stores/states';
import {
	Kebab,
	PageHead,
	Pager,
	SearchBox,
} from 'views/design-system';
import { ComplexEmailClientState } from 'views/email-client';
import { IsComplexStepRecipientInviteModalOpen } from 'views/multi-sign-agreement';
import { WelcomeNewUserModalState } from 'views/new-user-popup';
import { ConfigureSurvayFormState } from 'views/onboarding-flow';
import {
	AddedActionsState,
	IsConfigurationChecking,
	OnboardingFooterNavigateState,
	SelectedStepsState,
	UnConfiguredStep,
} from 'views/onboarding-flow/store';
import { ComplexSettingFormState } from 'views/pipeline/store';
import { PERMISSION_SCREEN, useUserRoles } from 'views/routes-children';
import { useBusinessCards } from 'views/settings';

import {
	OnboardingQRFlow,
	OnboardingQRTable,
	SelectedPipelineDetailsForQRState,
	useOnboardingQrFlow,
} from '../components';
import { ComplexStepsRecipientInviteView } from '../components/complex-step-recipient-invite';
import { ComplexStepsView } from '../components/complex-step-view';
import { SessionTreeGraph } from 'views/complex-onboarding-flow/components';
import { OnboardingInviteModal } from '../components/onboarding-invite-modal';
import { ConditionalOnboardingFlowState, IsQrInviteState } from '../components/store';
import { ErrorSessionsNotWrite } from '../constants';
import { ActivityDrawer } from './activity-drawer';
import { ConfigureDrawer } from './configure-drawer';
import { hasPayOut, stepKeyLine, toChainRows } from './derive';

import 'views/design-system/tokens.scss';
import 'views/design-system/surface.scss';
// the diagram's styling ships with the modal, which is code-split — without
// this the nodes would render unstyled (and 0px wide) on this route
import 'views/pipelines/components/complex-step-view/complex-step-view.scss';
import './onboarding-deck.scss';

const PAGE_LIMIT = 10;

/** The eleven step filters, verbatim from the shared filter popup. */
const STEP_FILTERS: { label: string; value: string }[] = [
	{ label: 'KYC', value: 'kyc' },
	{ label: 'AML', value: 'aml' },
	{ label: 'KYB', value: 'kyb' },
	{ label: 'Accred.(506b)', value: '506b' },
	{ label: 'Accred.(506c)', value: '506c' },
	{ label: 'Fund (Pay In)', value: 'payIn' },
	{ label: 'Fund (Pay Out)', value: 'payOut' },
	{ label: 'Sign Doc', value: 'signAgreement' },
	{ label: 'Form/Individual or Business Questionnaire.', value: 'form' },
	{ label: 'Questionnaire', value: 'dynamicForm' },
	{ label: 'KYB Document', value: 'kybForm' },
];

const SORTS: { label: string; value: string }[] = [
	{ label: 'Last updated', value: 'updatedAt' },
	{ label: 'Last created', value: 'createdAt' },
];

export const OnboardingDeck = () => {
	const navigate = useNavigate();
	const { PIPELINE, FLOW } = ROUTES;
	const { tillDate } = defineds ?? {};
	const { formatDate } = useTimezoneDate();

	/* ------------------------------------------------------------ page shell */
	const activeStepKeyName = useRecoilValue(ActiveStepState);
	const steps = useRecoilValue(PipelineStepsState);
	const setOpenedStepNames = useSetRecoilState(AllOpenedStepsNameState);
	const activeAction = useRecoilValue(ActiveActionKeyNameState);
	const resetComplexSettingForm = useResetRecoilState(ComplexSettingFormState);
	const resetComplexFlowConfiged = useResetRecoilState(ComplexFlowUnconfigedState);
	const resetConfigureSurvayFormState = useResetRecoilState(ConfigureSurvayFormState);
	const resetChequeFraudSelected = useResetRecoilState(isChecqueFraudSelected);
	const setSelectedDateRange = useSetRecoilState(SelectedDateRangeState);
	const setNodes = useSetRecoilState(NodesState);
	const setEdges = useSetRecoilState(EdgesState);
	const { trackEvents } = useTrackEvents();
	const { getDocuments } = useSignedDocs();

	/* ------------------------------------------------------------- list data */
	const [pipelines] = useRecoilState(AllPipelinesState);
	const [conditionalPipelines, setConditionalPipelines] = useRecoilState(
		AllConditionalPipelinesState
	);
	const selectedDateRange = useRecoilValue(SelectedDateRangeState);
	const [filterSortType, setFilterSortType] = useRecoilState(FilterSortTypeTableState);
	const [pipelinesFilter, setPipelinesFilter] = useRecoilState(pipelinesFiltersState);
	// An embedding host can turn the search box off (?isSearch=false).
	const { isSearch } = useRecoilValue(IframePropsState);
	const pageNumber = useRecoilValue(FundPaginationState);
	const setPageNumber = useSetRecoilState(FundPaginationState);
	const resetPageNumber = useResetRecoilState(FundPaginationState);
	const [credentials, setCredentials] = useRecoilState(ClientsCredentialsState);
	const credentialsLoadedStatus = useRecoilValue(CredentialsLoadedState);

	const {
		get: getComplexList,
		data: complexFlowList,
		loading: isComplexLoading,
		isLoaded: complexPipelinesLoaded,
	} = useNetwork();
	const { post: createCredentials } = useNetwork();
	const { remove: deleteComplexPipeline, loading: deletePipelineLoading } = useNetwork();

	const [search, setSearch] = useState('');
	const [pageCount, setPageCount] = useState(0);
	const [filterOpen, setFilterOpen] = useState(false);
	const filterRef = useRef<HTMLDivElement>(null);
	useOutsideClick(filterRef, () => setFilterOpen(false));

	/* --------------------------------------------------------------- actions */
	const setSelectedPipeline = useSetRecoilState(SelectedPipelineState);
	const setPipelineDetails = useSetRecoilState(SelectedPipelineDetails);
	const [pipelineDetails, setPipelineDetailsForQR] = useRecoilState(
		SelectedPipelineDetailsForQRState
	);
	const setSelectedPipelineIdForInviteInvestor = useSetRecoilState(PipelineIdState);
	const [, setIsModal] = useRecoilState(OpenInviteSelectorModal);
	const [, setIsComplex] = useRecoilState(ConditionalOnboardingFlowState);
	const [, setIsQrInviteFlow] = useRecoilState(IsQrInviteState);
	const setWelcomeNewUser = useSetRecoilState(WelcomeNewUserModalState);
	const setComplexEmailClient = useSetRecoilState(ComplexEmailClientState);
	const isComplexRecipientModalOpen = useRecoilValue(IsComplexStepRecipientInviteModalOpen);
	const businessCard = useRecoilValue(BusinessPaymentCardState);
	const { isVerifiedPhone, ...user } = useRecoilValue(loginState);

	const setNavigateFooter = useSetRecoilState(OnboardingFooterNavigateState);
	const setSelectedSteps = useSetRecoilState(SelectedStepsState);
	const setAddedAction = useSetRecoilState(AddedActionsState);
	const setUnOpennedConfig = useSetRecoilState(UnConfiguredStep);
	const setIsChecking = useSetRecoilState(IsConfigurationChecking);
	const resetSelectedComplexOnboarding = useResetRecoilState(SelectedOnboardingAction);

	const { successNotification, errorNotification, warningNotification } = useNotification();
	const { intialSetup } = useComplexLayout();
	const { invitationSubscriptionCheck } = useFreePlan();
	const { checkSubscription } = useSubscription();
	const { isAllowedToInvite } = useAllowedInvites();
	const { fetchBusinessCard } = useBusinessCards();
	const { checkUserWritePermission } = useUserRoles();
	const { handleNext } = useOnboardingQrFlow();
	const { database } = useFirebaseInit();

	const [editLoading, setEditLoading] = useState<string[]>([]);
	const [configureOpen, setConfigureOpen] = useState(false);
	const [activityFlow, setActivityFlow] = useState<{ _id: string; name: string } | null>(null);
	const [flowView, setFlowView] = useState<any>(null);
	const [collapsed, setCollapsed] = useState<string[]>([]);
	const [lockBlockedModal, setLockBlockedModal] = useState<{ open: boolean; lock?: any }>({
		open: false,
		lock: null,
	});
	const [deleteAction, setDeleteAction] = useState<{
		id: string | null;
		modalOpen: boolean;
	}>({ id: null, modalOpen: false });

	const isUserPermissionWrite = useMemo(
		() => checkUserWritePermission(PERMISSION_SCREEN.Onboarding),
		[checkUserWritePermission]
	);
	const isSessionWritePermission = useMemo(
		() => checkUserWritePermission(PERMISSION_SCREEN.Sessions),
		[checkUserWritePermission]
	);

	const { endDate, startDate }: any = selectedDateRange[0] ?? {};

	/* ---------------------------------------------------------------- effects */

	// "ALL" is a real range, not an absent one: the picker is seeded wide open so
	// the list shows every flow until the user narrows it.
	useEffect(() => {
		getDocuments();
		setSelectedDateRange([
			{ startDate: new Date(2021, 0, 1), endDate: tillDate, key: 'selection' },
		]);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		setOpenedStepNames(prev =>
			prev.includes(activeStepKeyName.key) ? [...prev] : [...prev, activeStepKeyName.key]
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeStepKeyName, steps, activeAction]);

	// Builder-store resets. Skipping any of these leaks state into the builder —
	// stale nodes/edges, unconfigured-step ghosts from the last flow edited.
	useEffect(() => {
		setIsChecking(false);
		setNavigateFooter('complex-flow');
		setUnOpennedConfig(['default']);
		setSelectedSteps([]);
		resetSelectedComplexOnboarding();
		setAddedAction([]);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [navigate]);

	useEffect(() => {
		fetchBusinessCard();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// A business with no credentials gets one made for it; the guard is what
	// stops a second one being created on every render.
	useEffect(() => {
		if (!credentials.length && credentialsLoadedStatus) {
			createCredentials(`${API_URL.CREDENTIAL}`, {}).then(resp => {
				setCredentials(prev => [...prev, resp]);
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [credentials, credentialsLoadedStatus]);

	const loadFlows = useCallback(
		(term?: string, isPagination = false) => {
			const searchTerm = term ?? search;
			const searchParam = searchTerm ? `&search=${searchTerm}` : '';
			const url = `${API_URL.COMPLEX_FLOW}?isDeleted=false&page=${
				searchTerm && !isPagination ? 1 : pageNumber
			}&limit=${PAGE_LIMIT}${searchParam}&startDate=${getOffDate(
				startDate,
				'start'
			)}&endDate=${getOffDate(endDate, 'end')}&dateFilterBy=${filterSortType?.value}`;
			getComplexList(url);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[endDate, startDate, pageNumber, filterSortType, search]
	);

	useEffect(() => {
		loadFlows(search, true);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pageNumber, endDate, startDate, filterSortType]);

	// A new search or date range starts at page 1 — page 4 of the old query is
	// not page 4 of the new one.
	useEffect(() => {
		resetPageNumber();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [endDate, startDate, search]);

	useEffect(() => {
		const id = window.setTimeout(() => loadFlows(search), 500);
		return () => window.clearTimeout(id);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [search]);

	useEffect(() => {
		if (!isComplexLoading && complexFlowList?.data?.[0]?.result) {
			setConditionalPipelines(complexFlowList.data[0].result);
			setPageCount(complexFlowList.data[0].totalPages ?? 0);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [complexFlowList, isComplexLoading]);

	/* ----------------------------------------------------------------- rows */

	const combined = useMemo(
		() => [
			...pipelines.map(item => ({ ...item, type: 'linear' })),
			...conditionalPipelines.map(item => ({ ...item, type: 'complex' })),
		],
		[conditionalPipelines, pipelines]
	);

	// Same contract the shared table used: a flow survives the filter only if it
	// contains every checked step.
	const rows = useMemo(() => {
		const checked = Object.keys(pipelinesFilter ?? {});
		if (!checked.length) return combined;
		return combined.filter((flow: any) =>
			checked.every(key => (flow.fullStatus ?? []).includes(key))
		);
	}, [combined, pipelinesFilter]);

	/* --------------------------------------------------------------- actions */

	const handleOpen = useCallback(() => {
		resetComplexSettingForm();
		resetComplexFlowConfiged();
		trackEvents('create-new-onboarding', {});
		navigate(`${PIPELINE}${FLOW}`);
		setEdges([]);
		setNodes([]);
		resetConfigureSurvayFormState();
		resetChequeFraudSelected();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [navigate]);

	const handleInvite = useCallback(
		(event: any, item: any) => {
			event.stopPropagation();
			if (!item || !item.isInviteAllowed || !isSessionWritePermission) return;
			// Order matters: billing walls come before the credit wall, so a user
			// without a plan is told about the plan, not about credits.
			if (invitationSubscriptionCheck) {
				if (!checkUserWritePermission(PERMISSION_SCREEN.Billing)) {
					warningNotification('You need billing permission.');
					return;
				}
				navigate(ROUTES.BILLING);
				return;
			}
			if (!isAllowedToInvite('onboarding')) {
				setWelcomeNewUser({
					open: true,
					type: 'Credits_Remain',
					serviceName: 'Onboarding Service',
				});
				return;
			}
			setIsComplex(item.type === 'complex');
			setPipelineDetails(item);
			setSelectedPipelineIdForInviteInvestor(item._id);
			setIsModal(true);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			checkSubscription,
			invitationSubscriptionCheck,
			isAllowedToInvite,
			isVerifiedPhone,
			isSessionWritePermission,
		]
	);

	const handleQr = useCallback(
		(event: any, item: any) => {
			event.stopPropagation();
			if (invitationSubscriptionCheck) {
				if (!checkUserWritePermission(PERMISSION_SCREEN.Billing)) {
					warningNotification('You need billing permission.');
					return;
				}
				navigate(ROUTES.BILLING);
				return;
			}
			if (!isAllowedToInvite('onboarding')) {
				setWelcomeNewUser({
					open: true,
					type: 'Credits_Remain',
					serviceName: 'Onboarding Service',
				});
				return;
			}
			setPipelineDetailsForQR(item);
			setPipelineDetails(item);
			setIsQrInviteFlow(true);
			handleNext(item, 'table');
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[isVerifiedPhone, invitationSubscriptionCheck, businessCard?.card, isAllowedToInvite]
	);

	/**
	 * Edit takes the flow's Firebase lock first. Someone else holding it opens
	 * the Flow Locked modal instead of the builder — two people in one flow
	 * overwrite each other's nodes.
	 */
	const handleModifyAndClone = useCallback(
		async (e: any, item: any, type: 'modify' | 'clone') => {
			if (!item.isEditAllowed) return;
			e.stopPropagation();
			if (type === 'modify') {
				setComplexEmailClient(item.emailClient);
				setEditLoading(prev => [...prev, item._id]);
				const { success, lock } = await acquireOnboardingLock(database, item._id, user);
				setEditLoading(prev => prev.filter(id => id !== item._id));
				if (success && !!lock && lock.userId !== user.userId) {
					setLockBlockedModal({ open: true, lock });
					return;
				}
			}
			intialSetup({ ...item, type });
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[database, user, intialSetup]
	);

	const handleConfigure = useCallback(
		(item: any) => {
			setSelectedPipeline(item);
			setConfigureOpen(true);
		},
		[setSelectedPipeline]
	);

	const handleDelete = useCallback((item: any) => {
		setDeleteAction({ id: item._id, modalOpen: true });
	}, []);

	const confirmDelete = useCallback(
		async (isOpen: boolean, value: boolean) => {
			const { id } = deleteAction;
			if (value && id) {
				const resp = await deleteComplexPipeline(`${API_URL.COMPLEX_FLOW}/${id}`);
				if (resp?.success || resp?._id || resp?.message === 'ok') {
					setConditionalPipelines(prev => prev.filter((el: any) => el._id !== id));
					successNotification('Onboarding Flow Deleted.');
				} else {
					errorNotification('Could not delete this onboarding flow.');
				}
			}
			setDeleteAction({ id: null, modalOpen: isOpen && !value });
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[deleteAction]
	);

	const toggleFilter = useCallback(
		(value: string) => {
			setPipelinesFilter((prev: any) => {
				const next = { ...(prev ?? {}) };
				if (next[value]) delete next[value];
				else next[value] = ['pending', 'processing', 'completed', 'rejected'];
				return next;
			});
		},
		[setPipelinesFilter]
	);

	const kebabFor = useCallback(
		(flow: any) => {
			const payOut = hasPayOut(flow);
			const items: { label: string; onClick: () => void }[] = [];
			if (flow.type === 'complex') {
				items.push({
					label: flow.isEditAllowed ? 'Duplicate flow' : 'Duplicate flow (locked)',
					onClick: () => handleModifyAndClone({ stopPropagation: () => ({}) }, flow, 'clone'),
				});
			}
			items.push({
				label: payOut ? 'Generate QR (blocked by Pay Out)' : 'Generate QR',
				onClick: () => {
					if (payOut) {
						warningNotification(
							'You are unable to generate a QR code due to an ongoing Fund Pay Out process in this flow.'
						);
						return;
					}
					handleQr({ stopPropagation: () => ({}) }, flow);
				},
			});
			items.push({ label: 'Configure', onClick: () => handleConfigure(flow) });
			items.push({
				label: 'Activity Log',
				onClick: () => setActivityFlow({ _id: flow._id, name: flow.name }),
			});
			items.push({ label: 'Delete', onClick: () => handleDelete(flow) });
			return items;
		},
		[handleConfigure, handleDelete, handleModifyAndClone, handleQr, warningNotification]
	);

	const activeFilters = Object.keys(pipelinesFilter ?? {}).length;
	const isLoading = isComplexLoading;
	const isEmpty = !isLoading && complexPipelinesLoaded && !rows.length;

	return (
		<div className="sv2-scope sv2 onboarding-deck">
			<PageHead
				title="Onboarding"
				sub={
					<>
						{conditionalPipelines.length} onboarding flow
						{conditionalPipelines.length === 1 ? '' : 's'} &middot; page{' '}
						{pageNumber} of {Math.max(pageCount, 1)} &middot; list reads live from{' '}
						<code>/complex-flow</code> &middot; flows are searched by name
					</>
				}
				action={
					isUserPermissionWrite ? (
						<button className="cta" type="button" onClick={handleOpen}>
							+ Create New
						</button>
					) : null
				}
			/>

			<div className="ctl">
				<span
					className="pre"
					onClick={() => {
						// Sorting mid-flight would land the old page under the new order.
						if (isComplexLoading) return;
						setFilterSortType(
							filterSortType?.value === 'updatedAt'
								? (SORTS[1] as any)
								: (SORTS[0] as any)
						);
					}}
				>
					Sort:{' '}
					<b>
						{SORTS.find(s => s.value === filterSortType?.value)?.label ?? 'Last updated'}
					</b>{' '}
					&#9662;
				</span>

				<div ref={filterRef} className="onboarding-deck__filter">
					<span
						className={`pre${activeFilters ? ' on' : ''}`}
						onClick={() => setFilterOpen(v => !v)}
					>
						Filter{activeFilters ? ` · ${activeFilters} selected` : ''}
					</span>
					{filterOpen && (
						<div className="kmenu onboarding-deck__filter-menu">
							<div className="onboarding-deck__filter-head">
								Steps in flow
								<button
									className="reinv"
									type="button"
									onClick={() => setPipelinesFilter({})}
								>
									Reset
								</button>
							</div>
							{STEP_FILTERS.map(f => (
								<label key={f.value} className="onboarding-deck__filter-row">
									<input
										type="checkbox"
										checked={!!pipelinesFilter?.[f.value]}
										onChange={() => toggleFilter(f.value)}
									/>
									{f.label}
								</label>
							))}
						</div>
					)}
				</div>

				<DatePicker stateType={SelectedDateRangeState} />

				{isSearch !== 'false' && (
					<SearchBox
						value={search}
						onChange={setSearch}
						title="Searches flow name"
						placeholder="Search"
					/>
				)}

				<Pager
					page={pageNumber}
					pages={Math.max(pageCount, 1)}
					onChange={setPageNumber}
				/>
			</div>

			<div className="panel">
				<div className="tscroll">
					<table className="stbl">
						<thead>
							<tr>
								<th className="odcol">Onboarding Details</th>
								<th className="flcol">Steps In Flow</th>
								{isUserPermissionWrite && <th className="accol">Actions</th>}
							</tr>
						</thead>
						<tbody>
							{rows.map((flow: any) => {
								const chainRows = toChainRows(flow);
								const instances = (flow.instances ?? []).filter(Boolean);
								const open = instances.length > 0 && !collapsed.includes(flow._id);
								return (
									<Fragment key={flow._id}>
										<tr>
											<td className="od odcol">
												<span className="nm">{flow.name || '-a-'}</span>
												<br />
												<span className="fund">
													{flow.sessionCount
														? `${flow.sessionCount} session${
																flow.sessionCount === 1 ? '' : 's'
														  }`
														: 'no sessions yet'}
												</span>
												<div className="meta">
													<b>Created</b> :{' '}
													{flow.createdAt
														? `${formatDate(flow.createdAt, 'hh:mm a', true)} | ${formatDate(
																flow.createdAt,
																'MMM dd, yyyy'
														  )}`
														: '--'}
													<br />
													<b>Updated</b> :{' '}
													{flow.updatedAt
														? `${formatDate(flow.updatedAt, 'hh:mm a', true)} | ${formatDate(
																flow.updatedAt,
																'MMM dd, yyyy'
														  )}`
														: '--'}
												</div>
												{instances.length > 0 && (
													<span
														className="msb"
														onClick={() =>
															setCollapsed(prev =>
																prev.includes(flow._id)
																	? prev.filter(id => id !== flow._id)
																	: [...prev, flow._id]
															)
														}
													>
														{open ? '▾' : '▸'} {instances.length} QR instance
														{instances.length === 1 ? '' : 's'}
													</span>
												)}
											</td>

											<td className="fl flcol">
												{/* The same diagram the sessions list and the flow overview
													draw. Pills could not put a branching flow on one row —
													each branch needed a row of its own — where the graph
													lays the branches out inside a single lane. */}
												<div
													className={`conditional-flow-steps rowflow${
														chainRows.length > 1 ? ' rowflow--branched' : ''
													}`}
												>
													<SessionTreeGraph
														details={flow}
														isSessionModal
														isSessionList
													/>
												</div>
												<div className="fnm">
													<span className="vcf" onClick={() => setFlowView(flow)}>
														View complete flow
													</span>
													{stepKeyLine(flow)}
												</div>
											</td>

											{isUserPermissionWrite && (
												<td className="accol">
													<button
														className="reinv"
														type="button"
														title={
															!isSessionWritePermission
																? ErrorSessionsNotWrite
																: !flow.isInviteAllowed
																? 'Inviting is not available for this flow'
																: 'Invite investors to this flow'
														}
														disabled={!flow.isInviteAllowed || !isSessionWritePermission}
														onClick={e => handleInvite(e, flow)}
													>
														Invite
													</button>
													{flow.type === 'complex' &&
														(editLoading.includes(flow._id) ? (
															<Loader type="loader" dimension={16} />
														) : (
															<button
																className="reinv"
																type="button"
																title={
																	flow.isEditAllowed
																		? 'Edit your Onboarding Flow'
																		: 'This flow cannot be edited'
																}
																disabled={!flow.isEditAllowed}
																onClick={e => handleModifyAndClone(e, flow, 'modify')}
															>
																Edit
															</button>
														))}
													<Kebab items={kebabFor(flow)} />
												</td>
											)}
										</tr>

										{open && (
											<tr className="onboarding-deck__qr-row">
												<td colSpan={isUserPermissionWrite ? 3 : 2} className="qr-sub">
													<div className="panel">
														<div className="ph">
															QR instances
															<span className="r">
																{instances.length} configured
															</span>
														</div>
														<OnboardingQRTable
															sv2
															isLoadingQrList={false}
															piplineData={flow}
															selectedPipelineQr={instances}
															combinedOnboarding={combined}
														/>
													</div>
												</td>
											</tr>
										)}
									</Fragment>
								);
							})}
						</tbody>
					</table>
				</div>

				{isLoading && <div className="sv2-loading">Loading onboarding flows…</div>}
				{isEmpty && (
					<div className="sv2-empty">
						{search || activeFilters ? (
							'No onboarding flow matches this search.'
						) : (
							<>
								No Onboarding Flow Available
								<br />
								You have not created any onboarding flow yet.
								{isUserPermissionWrite && (
									<>
										<br />
										<br />
										<button className="cta" type="button" onClick={handleOpen}>
											+ Create New
										</button>
									</>
								)}
							</>
						)}
					</div>
				)}
			</div>

			<div className="foot">
				Each row draws the flow itself &mdash; the same graph as the overview,
				so a flow that branches shows both paths in place rather than as a list.
				Flows with QR instances grow an instance panel under the row.{' '}
				<code>View complete flow</code> opens the full graph.
			</div>

			{/* ---- drawers ---- */}
			<ConfigureDrawer open={configureOpen} onClose={() => setConfigureOpen(false)} />
			<ActivityDrawer
				open={!!activityFlow}
				flow={activityFlow}
				onClose={() => setActivityFlow(null)}
			/>

			{/* ---- modals, mounted exactly as the list has always mounted them ---- */}
			<Confirmation
				handleModal={confirmDelete}
				visible={deleteAction.modalOpen}
				description="You want to delete this onboarding flow?"
				label="Delete"
				title="Are you sure?"
				secondryLabel="Close"
				loading={deletePipelineLoading}
			/>

			<OnboardingInviteModal from={'onboardings'} />
			<OnboardingQRFlow />

			{!!flowView && (
				<ComplexStepsView
					isOpen={!!flowView}
					closeModal={() => setFlowView(null)}
					details={flowView}
					isOnboardingList
				/>
			)}

			{isComplexRecipientModalOpen && (
				<ComplexStepsRecipientInviteView
					isOpen={isComplexRecipientModalOpen}
					closeModal={() => setFlowView(null)}
					details={pipelineDetails}
					isOnboardingList
				/>
			)}

			{lockBlockedModal.open && lockBlockedModal.lock && (
				<Modal
					isOpen={lockBlockedModal.open && !!lockBlockedModal.lock}
					title=""
					modalName="Flow Locked"
					className="onboarding-details"
					closeModal={() => setLockBlockedModal({ open: false })}
				>
					<div className="onboarding-details__lock-blocked">
						<Image fileName="logic_flow.svg" />
						<div>
							<h4>
								{lockBlockedModal.lock.userName || 'Someone else'} (
								{lockBlockedModal.lock.userEmail}) is currently editing this onboarding
								flow.
							</h4>
							<p>
								To avoid conflicts, you’ll be able to make changes once they finish and
								exit the editor.
							</p>
						</div>
						<Button
							type="onboarding-details__lock-blocked-close-btn"
							handleClick={() => setLockBlockedModal({ open: false })}
							label={'Close'}
						/>
					</div>
				</Modal>
			)}
		</div>
	);
};

export default OnboardingDeck;
